import { numOrNull } from './rpc';
import { isRole, type Role } from './permissions';
import { fromDb } from '@/lib/loadStatus';
import { isDocumentKind } from '@/lib/loadDocs';
import {
  INVITE_STATES,
  MEMBER_STATUSES,
  PAYERS,
  PAYMENT_METHODS,
  RECEIVABLE_DISPLAY_STATUSES,
  RECEIVABLE_STATUSES,
  type InvoicePayment,
  type Member,
  type MemberInvite,
  type MyInvite,
  type Profile,
  type Receipt,
  type Receivable,
  CARRIER_STATUSES,
  DRIVER_STATUSES,
  OWNER_STATUSES,
  SEAT_STATUSES,
  SETUP_STATUSES,
  TRUCK_STATUSES,
  toStatus,
  type Broker,
  type BrokerSetup,
  type Carrier,
  type CarrierStatus,
  type Driver,
  type Invite,
  type Owner,
  type Seat,
  type Truck,
  TASK_PRIORITIES,
  TASK_STATUSES,
  type Load,
  type LoadDocument,
  type LoadEvent,
  type LoadStop,
  type Task,
} from './types';

/**
 * snake_case rows (as PostgREST delivers them) -> the camelCase domain types. One mapper per table,
 * shared by every hook that reads it. `row` keeps the raw record for the edit forms, whose keys are
 * the field-spec (backend) keys.
 */

export type Row = Record<string, unknown>;

const str = (v: unknown): string | null => (typeof v === 'string' ? v : v === null || v === undefined ? null : String(v));
const req = (v: unknown): string => str(v) ?? '';
const numeric = (v: unknown): number | null => numOrNull(v as number | string | null | undefined);

/**
 * The whitelisted carrier columns (the carrier_update keys) plus what the list shows. `notes:office_notes`:
 * since 0154/0155 the office note is not on the row (always null there) but in carrier_office_notes, read
 * through the computed field office_notes (the same for drivers and loads). Aliased back to `notes`.
 */
export const CARRIER_COLUMNS =
  'id, legal_name, dba, mc_number, dot_number, ein, address_line1, address_line2, city, state, postal_code, ' +
  'contact_name, contact_phone, contact_email, factoring_company, payment_terms_days, minimum_rate_per_mile, ' +
  'billing_email, document_locale, factoring_address, factoring_phone, factoring_email, broker_invoice_prefix, ' +
  'broker_invoice_next, owner_id, notes:office_notes, status, updated_at';

export const OWNER_COLUMNS =
  'id, legal_name, dba, ein, address_line1, address_line2, city, state, postal_code, contact_name, ' +
  'contact_phone, contact_email, notes, status, updated_at';

export const TRUCK_COLUMNS =
  'id, carrier_id, unit_number, vin, year, make, model, plate, plate_state, vehicle_type, cargo_type, ' +
  'max_weight, length_ft, width_ft, height_ft, owner_id, status, updated_at';

export const DRIVER_COLUMNS = 'id, name, phone, email, license_number, license_state, status, notes:office_notes, user_id';

export const BROKER_COLUMNS =
  'id, name, mc_number, dot_number, address_line1, address_line2, city, state, postal_code, phone, email, ' +
  'billing_email, website, notes, updated_at';

export const SETUP_COLUMNS = 'id, carrier_id, broker_id, status, setup_date, last_verified_at, notes, updated_at';

/** `code_hash` is not granted to the portal: the invite read names its columns, never `*`. */
export const INVITE_COLUMNS = 'id, driver_id, created_at, expires_at, used_at, revoked_at';

export function toCarrier(r: Row): Carrier {
  return {
    id: req(r.id),
    legalName: req(r.legal_name),
    dba: str(r.dba),
    mcNumber: str(r.mc_number),
    dotNumber: str(r.dot_number),
    ownerId: str(r.owner_id),
    status: toStatus(r.status, CARRIER_STATUSES, 'inactive'),
    row: r,
  };
}

export function toOwner(r: Row): Owner {
  return {
    id: req(r.id),
    legalName: req(r.legal_name),
    dba: str(r.dba),
    ein: str(r.ein),
    contactName: str(r.contact_name),
    contactPhone: str(r.contact_phone),
    contactEmail: str(r.contact_email),
    status: toStatus(r.status, OWNER_STATUSES, 'inactive'),
    row: r,
  };
}

export function toTruck(r: Row): Truck {
  return {
    id: req(r.id),
    carrierId: req(r.carrier_id),
    unitNumber: req(r.unit_number),
    vin: str(r.vin),
    year: numeric(r.year),
    make: str(r.make),
    model: str(r.model),
    plate: str(r.plate),
    plateState: str(r.plate_state),
    vehicleType: str(r.vehicle_type),
    cargoType: str(r.cargo_type),
    lengthFt: numeric(r.length_ft),
    widthFt: numeric(r.width_ft),
    heightFt: numeric(r.height_ft),
    ownerId: str(r.owner_id),
    status: toStatus(r.status, TRUCK_STATUSES, 'inactive'),
    row: r,
  };
}

export function toDriver(r: Row): Driver {
  return {
    id: req(r.id),
    name: req(r.name),
    phone: str(r.phone),
    email: str(r.email),
    licenseNumber: str(r.license_number),
    licenseState: str(r.license_state),
    status: toStatus(r.status, DRIVER_STATUSES, 'inactive'),
    notes: str(r.notes),
    userId: str(r.user_id),
    row: r,
  };
}

export function toSeat(r: Row): Seat {
  return {
    carrierId: req(r.carrier_id),
    driverId: req(r.driver_id),
    status: toStatus(r.status, SEAT_STATUSES, 'ended'),
    startedOn: str(r.started_on),
    endedOn: str(r.ended_on),
  };
}

export function toInvite(r: Row): Invite {
  return { id: req(r.id), driverId: req(r.driver_id), createdAt: req(r.created_at), expiresAt: req(r.expires_at) };
}

export function toBroker(r: Row): Broker {
  return {
    id: req(r.id),
    name: req(r.name),
    mcNumber: str(r.mc_number),
    dotNumber: str(r.dot_number),
    city: str(r.city),
    state: str(r.state),
    phone: str(r.phone),
    email: str(r.email),
    row: r,
  };
}

export function toSetup(r: Row): BrokerSetup {
  return {
    id: req(r.id),
    carrierId: req(r.carrier_id),
    brokerId: req(r.broker_id),
    status: toStatus(r.status, SETUP_STATUSES, 'not_started'),
    setupDate: str(r.setup_date),
    lastVerifiedAt: str(r.last_verified_at),
    notes: str(r.notes),
    virtual: false,
  };
}

/** A carrier as a select option or a name lookup. */
export interface CarrierOption {
  id: string;
  legalName: string;
  status: CarrierStatus;
}

export function toCarrierOption(r: Row): CarrierOption {
  return { id: req(r.id), legalName: req(r.legal_name), status: toStatus(r.status, CARRIER_STATUSES, 'inactive') };
}

/** Group rows by a key (a Map keeps the lookup O(1) in the derived values). */
export function groupBy<T>(items: T[], key: (t: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const it of items) {
    const k = key(it);
    const list = out.get(k);
    if (list) list.push(it);
    else out.set(k, [it]);
  }
  return out;
}

/** The rows of a read. The column lists above are plain strings, which supabase-js cannot parse into a
 *  row type, so the data arrives untyped; the mappers above give it its shape. */
export function asRows(data: unknown): Row[] {
  return (data ?? []) as Row[];
}

// --- team (0074..0077) ---------------------------------------------------------------------------

const ids = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const localeOf = (v: unknown): 'en' | 'es' => (v === 'es' ? 'es' : 'en');
const roleOf = (v: unknown): Role => (isRole(v) ? v : 'dispatcher');

/** The `user_profiles` columns the portal reads (own row only, by RLS and by filter). */
export const PROFILE_COLUMNS = 'user_id, display_name, phone, locale';

/** An `agency_team` row. */
export function toMember(r: Row): Member {
  return {
    userId: req(r.user_id),
    role: roleOf(r.role),
    status: toStatus(r.status, MEMBER_STATUSES, 'inactive'),
    displayName: str(r.display_name),
    email: req(r.email),
    phone: str(r.phone),
    joinedAt: req(r.joined_at),
    invitedBy: str(r.invited_by),
    deactivatedAt: str(r.deactivated_at),
    carrierIds: ids(r.carrier_ids),
    isSelf: r.is_self === true,
    // Every sign-in method carries an email; only delete_my_account (0086) nulls it.
    deleted: r.email === null || r.email === undefined || r.email === '',
  };
}

/** An `agency_member_invites` row. */
export function toMemberInvite(r: Row): MemberInvite {
  return {
    inviteId: req(r.invite_id),
    email: req(r.email),
    role: roleOf(r.role),
    displayName: str(r.display_name),
    locale: localeOf(r.locale),
    carrierIds: ids(r.carrier_ids),
    invitedBy: str(r.invited_by),
    invitedByName: str(r.invited_by_name),
    createdAt: req(r.created_at),
    expiresAt: req(r.expires_at),
    state: toStatus(r.state, INVITE_STATES, 'superseded'),
    sendCount: numeric(r.send_count) ?? 0,
    lastRequestedAt: str(r.last_requested_at),
    lastSentAt: str(r.last_sent_at),
    lastFailedAt: str(r.last_failed_at),
    lastError: str(r.last_error),
  };
}

/** A `my_member_invites()` row. */
export function toMyInvite(r: Row): MyInvite {
  return {
    inviteId: req(r.invite_id),
    agencyId: req(r.agency_id),
    agencyName: req(r.agency_name),
    role: roleOf(r.role),
    inviterName: str(r.inviter_name),
    expiresAt: req(r.expires_at),
  };
}

export function toProfile(r: Row): Profile {
  return { userId: req(r.user_id), displayName: req(r.display_name), phone: str(r.phone), locale: localeOf(r.locale) };
}

// --- ledgers (0069..0073) ------------------------------------------------------------------------------

const money = (v: unknown): number => numeric(v) ?? 0;
const intOrNull = (v: unknown): number | null => numeric(v);

/** An `invoice_payments_read` row. */
export function toInvoicePayment(r: Row): InvoicePayment {
  return {
    id: req(r.id),
    invoiceId: req(r.invoice_id),
    kind: r.kind === 'write_off' ? 'write_off' : 'payment',
    amount: money(r.amount),
    paidOn: req(r.paid_on),
    method: r.method === null || r.method === undefined ? null : toStatus(r.method, PAYMENT_METHODS, 'other'),
    reference: str(r.reference),
    notes: str(r.notes),
    recordedBy: str(r.recorded_by),
    recordedAt: req(r.recorded_at),
    reversedAt: str(r.reversed_at),
    reversedBy: str(r.reversed_by),
    reversalReason: str(r.reversal_reason),
  };
}

/** The `broker_receivables_read` columns of a receivable (the hook adds fee_held, notes and the derived fields). */
export type ReceivableBase = Omit<Receivable, 'feeHeld' | 'notes' | 'tab' | 'zeroBalance' | 'dispatcherUserId' | 'dispatcherName'>;

export function toReceivableBase(r: Row): ReceivableBase {
  return {
    loadId: req(r.load_id),
    loadNumber: req(r.load_number),
    carrierId: req(r.carrier_id),
    carrierName: str(r.carrier_name),
    brokerId: str(r.broker_id),
    brokerName: str(r.broker_name),
    invoiceNumber: req(r.invoice_number),
    invoicedOn: req(r.invoiced_on),
    dueOn: req(r.due_on),
    amount: money(r.amount),
    received: money(r.received),
    balance: money(r.balance),
    status: toStatus(r.status, RECEIVABLE_STATUSES, 'open'),
    displayStatus: toStatus(r.display_status, RECEIVABLE_DISPLAY_STATUSES, 'open'),
    factored: r.factored === true,
    factoringCompany: str(r.factoring_company),
    disputedAt: str(r.disputed_at),
    disputeReason: str(r.dispute_reason),
    shortAmount: numeric(r.short_amount),
    shortReason: str(r.short_reason),
    closedOn: str(r.closed_on),
    lastPaidOn: str(r.last_paid_on),
    daysOutstanding: intOrNull(r.days_outstanding),
    daysToPay: intOrNull(r.days_to_pay),
  };
}

/** A `broker_receivable_payments_read` row. */
export function toReceipt(r: Row): Receipt {
  return {
    id: req(r.id),
    loadId: req(r.load_id),
    amount: money(r.amount),
    paidOn: req(r.paid_on),
    payer: toStatus(r.payer, PAYERS, 'broker'),
    reference: str(r.reference),
    notes: str(r.notes),
    recordedBy: str(r.recorded_by),
    recordedAt: req(r.recorded_at),
    reversedAt: str(r.reversed_at),
    reversedBy: str(r.reversed_by),
    reversalReason: str(r.reversal_reason),
  };
}

/** The `loads` columns the broker ledger reads (dispatcher, rate, the ready-to-invoice status). */
export const LEDGER_LOAD_COLUMNS =
  'id, agency_id, load_number, carrier_id, broker_id, dispatcher_user_id, status, linehaul_amount, accessorial_amount, updated_at';

// --- loads (portal-loads, design 3.1) --------------------------------------------------------------

/** The `loads` columns the screens read (never the deprecated broker_* columns, never `*`). */
export const LOAD_COLUMNS =
  'id, agency_id, carrier_id, driver_id, truck_id, dispatcher_user_id, broker_id, load_number, status, commodity, ' +
  'weight, loaded_miles, deadhead_miles, linehaul_amount, accessorial_amount, tonu_amount, detention_amount, lumper_amount, ' +
  'other_amount, broker_reference, notes:office_notes, driver_responded_at, created_at, updated_at, is_test';

export const STOP_COLUMNS =
  'load_id, seq, kind, company, address_line1, city, state, postal_code, contact_name, contact_phone, ' +
  'reference_number, notes, appointment_at, arrived_at, departed_at, latitude, longitude';

export const EVENT_COLUMNS = 'id, load_id, from_status, to_status, kind, detail, actor_user_id, entered_by, at';

export const DOCUMENT_COLUMNS =
  'id, load_id, kind, storage_path, file_name, mime_type, size_bytes, uploaded_by, created_at, archived_at, archived_by, replaced_by';

export const TASK_COLUMNS =
  'id, load_id, carrier_id, driver_id, title, detail, priority, status, due_at, source, created_at, assigned_user_id, created_by, completed_at';


export function toLoad(r: Row): Load {
  const accessorial = numeric(r.accessorial_amount) ?? 0;
  const tonu = numeric(r.tonu_amount) ?? 0;
  const detention = numeric(r.detention_amount) ?? 0;
  const lumper = numeric(r.lumper_amount) ?? 0;
  // A row without the itemized parts (other_amount is NOT NULL since 0137, so only a pre-0137 reader's
  // row) reads its lump as "other", as the 0137 backfill did.
  const other =
    r.other_amount === undefined || r.other_amount === null ? (Math.round(accessorial * 100) - Math.round((tonu + detention + lumper) * 100)) / 100 : (numeric(r.other_amount) ?? 0);
  return {
    id: req(r.id),
    agencyId: req(r.agency_id),
    carrierId: req(r.carrier_id),
    driverId: str(r.driver_id),
    truckId: str(r.truck_id),
    dispatcherUserId: str(r.dispatcher_user_id),
    brokerId: str(r.broker_id),
    number: req(r.load_number),
    status: fromDb(r.status) ?? 'unknown',
    commodity: str(r.commodity),
    weight: intOrNull(r.weight),
    loadedMiles: intOrNull(r.loaded_miles),
    deadheadMiles: intOrNull(r.deadhead_miles),
    linehaul: numeric(r.linehaul_amount),
    accessorial,
    tonu,
    detention,
    lumper,
    other,
    brokerReference: str(r.broker_reference),
    notes: str(r.notes),
    driverRespondedAt: str(r.driver_responded_at),
    createdAt: req(r.created_at),
    updatedAt: req(r.updated_at),
    isTest: r.is_test === true,
  };
}

export function toStop(r: Row): LoadStop {
  return {
    loadId: req(r.load_id),
    seq: Number(r.seq),
    kind: r.kind === 'delivery' ? 'delivery' : 'pickup',
    company: str(r.company),
    addressLine1: str(r.address_line1),
    city: str(r.city),
    state: str(r.state),
    postalCode: str(r.postal_code),
    contactName: str(r.contact_name),
    contactPhone: str(r.contact_phone),
    referenceNumber: str(r.reference_number),
    notes: str(r.notes),
    appointmentAt: str(r.appointment_at),
    arrivedAt: str(r.arrived_at),
    departedAt: str(r.departed_at),
    // Both or neither (the table's check): a half pair is no pin.
    ...(() => {
      const lat = numeric(r.latitude);
      const lon = numeric(r.longitude);
      return lat !== null && lon !== null ? { latitude: lat, longitude: lon } : { latitude: null, longitude: null };
    })(),
  };
}

export function toEvent(r: Row): LoadEvent {
  const detail = r.detail && typeof r.detail === 'object' && !Array.isArray(r.detail) ? (r.detail as Record<string, unknown>) : {};
  return {
    id: req(r.id),
    loadId: req(r.load_id),
    kind: req(r.kind),
    from: fromDb(r.from_status),
    to: fromDb(r.to_status),
    detail,
    actorUserId: str(r.actor_user_id),
    enteredBy: str(r.entered_by),
    at: req(r.at),
  };
}

export function toDocument(r: Row): LoadDocument {
  return {
    id: req(r.id),
    loadId: req(r.load_id),
    kind: isDocumentKind(r.kind) ? r.kind : 'other',
    storagePath: req(r.storage_path),
    fileName: str(r.file_name),
    mimeType: str(r.mime_type),
    sizeBytes: numeric(r.size_bytes),
    uploadedBy: str(r.uploaded_by),
    createdAt: req(r.created_at),
    archivedAt: str(r.archived_at),
    archivedBy: str(r.archived_by),
    replacedBy: str(r.replaced_by),
    // 0156: the reason is in the office's load log (load_events), never on the row; useLoad joins it.
    archiveReason: null,
  };
}

export function toTask(r: Row): Task {
  return {
    id: req(r.id),
    loadId: str(r.load_id),
    carrierId: str(r.carrier_id),
    title: req(r.title),
    detail: str(r.detail),
    priority: toStatus(r.priority, TASK_PRIORITIES, 'normal'),
    status: toStatus(r.status, TASK_STATUSES, 'open'),
    dueAt: str(r.due_at),
    source: req(r.source),
    createdAt: req(r.created_at),
    assignedUserId: str(r.assigned_user_id),
    driverId: str(r.driver_id),
    createdBy: str(r.created_by),
    completedAt: str(r.completed_at),
  };
}

/** Split `ids` into chunks for `.in()` reads: 500 uuids in one query string is ~19 kB, over what a
 *  proxy's header buffers take; 100 is ~3.7 kB. */
export function chunks<T>(items: T[], size = 100): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}
